export type OrderStatus =
  | "new"
  | "preparing"
  | "almost_ready"
  | "ready"
  | "out_for_delivery"
  | "picked_up"
  | "not_picked";

export type PickupMethod = "dine_in" | "takeaway" | "car" | "delivery";
export type PaymentMethod = "card" | "apple_pay" | "wallet" | "cash" | "pos_terminal";
export type OrderSource = "app" | "pos";

export interface OrderItem {
  name: string;
  quantity: number;
  unitPrice: number;
}

export interface CarInfo {
  type: string;
  color: string;
  plate: string;
}

export interface Order {
  id: string;
  number: number;
  source: OrderSource;
  status: OrderStatus;
  pickupMethod: PickupMethod;
  paymentMethod: PaymentMethod;
  customerName?: string;
  customerPhone?: string;
  /** عنوان التوصيل بواسطة المحل — للعرض فقط (بدون مناديب أو تتبّع) */
  deliveryAddress?: string;
  carInfo?: CarInfo;
  notes?: string;
  items: OrderItem[];
  subtotal: number;
  discount: number;
  appFee: number;
  /** رسوم التوصيل بواسطة المحل — للعرض فقط (15.00 ر.س) */
  deliveryFee?: number;
  total: number;
  createdAt: string;
  minutesAgo: number;
  pickupRemainingSeconds?: number;
  /** تقييم العميل للمحل (نجوم) */
  rating?: number;
  /** تقييم الموظف للعميل (نجوم) */
  staffRating?: number;
  staffRatedBy?: string;
  staffRatedAt?: string;
  /** مديونية رسوم المنصة 0.80 ر.س على المحل */
  debtRecorded?: boolean;
  invoiceIssued?: boolean;
  /** مديونية العميل المسجّلة على محفظته عند تأكيد «لم يُستلم» */
  customerDebtAmount?: number;
  /** المبلغ الذي تم خصمه من محفظة العميل */
  walletDeducted?: number;
  /** المتبقي كرصيد سالب على محفظة العميل عند عدم كفاية الرصيد */
  walletShortfall?: number;
  /** إيقاف العميل عن إنشاء طلبات جديدة حتى سداد المديونية */
  customerBlocked?: boolean;
}

/**
 * مجموعة البيانات الموحّدة — تُستخدم في:
 * الرئيسية + قائمة الطلبات + تفاصيل الطلب + الإشعارات + التقارير
 */
export const mockOrders: Order[] = [
  {
    id: "o-1240",
    number: 1240,
    source: "app",
    status: "ready",
    pickupMethod: "takeaway",
    paymentMethod: "cash",
    customerName: "طلال الشمري",
    customerPhone: "0556677889",
    items: [
      { name: "برجر دبل", quantity: 1, unitPrice: 24 },
      { name: "بطاطس بالجبنة", quantity: 1, unitPrice: 11 },
      { name: "مشروب غازي", quantity: 1, unitPrice: 5 },
    ],
    subtotal: 40,
    discount: 0,
    appFee: 0.8,
    total: 40.8,
    createdAt: "14:24",
    minutesAgo: 18,
    pickupRemainingSeconds: 20,
    debtRecorded: true,
    invoiceIssued: false,
  },
  {
    id: "o-1236",
    number: 1236,
    source: "app",
    status: "new",
    pickupMethod: "takeaway",
    paymentMethod: "card",
    customerName: "فيصل العمري",
    items: [
      { name: "برجر كلاسيك", quantity: 1, unitPrice: 18 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
      { name: "مشروب غازي", quantity: 1, unitPrice: 5 },
    ],
    subtotal: 30,
    discount: 0,
    appFee: 0.8,
    total: 30.8,
    createdAt: "14:41",
    minutesAgo: 1,
    invoiceIssued: true,
  },
  {
    id: "o-1235",
    number: 1235,
    source: "app",
    status: "preparing",
    pickupMethod: "delivery",
    paymentMethod: "card",
    customerName: "سلطان بن نايف",
    customerPhone: "0503334455",
    deliveryAddress: "الرياض — حي الياسمين، شارع الأمير سلطان بن عبدالعزيز، مبنى 12، الدور الثاني، شقة 5",
    notes: "توصيل إلى العنوان المسجّل — الرجاء الاتصال عند الوصول.",
    items: [
      { name: "برجر كلاسيك", quantity: 2, unitPrice: 18 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
      { name: "مشروب غازي", quantity: 2, unitPrice: 5 },
    ],
    subtotal: 53,
    discount: 0,
    appFee: 0.8,
    deliveryFee: 15,
    total: 68.8,
    createdAt: "14:40",
    minutesAgo: 4,
    invoiceIssued: true,
  },
  {
    id: "o-1234",
    number: 1234,
    source: "app",
    status: "new",
    pickupMethod: "takeaway",
    paymentMethod: "card",
    customerName: "فهد العتيبي",
    items: [
      { name: "برجر كلاسيك", quantity: 2, unitPrice: 18 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
      { name: "مشروب غازي", quantity: 2, unitPrice: 5 },
    ],
    subtotal: 53,
    discount: 0,
    appFee: 0.8,
    total: 53.8,
    createdAt: "14:38",
    minutesAgo: 3,
    invoiceIssued: true,
  },
  {
    id: "o-1233",
    number: 1233,
    source: "app",
    status: "new",
    pickupMethod: "car",
    paymentMethod: "apple_pay",
    customerName: "نورة القحطاني",
    carInfo: { type: "لكزس LX", color: "أبيض", plate: "أ ب ج 4521" },
    notes: "الرجاء وضع الطلب في شنطة السيارة الخلفية.",
    items: [
      { name: "شاورما دجاج", quantity: 2, unitPrice: 16 },
      { name: "بطاطس بالجبنة", quantity: 1, unitPrice: 11 },
      { name: "عصير برتقال", quantity: 2, unitPrice: 8 },
    ],
    subtotal: 59,
    discount: 0,
    appFee: 0.8,
    total: 59.8,
    createdAt: "14:36",
    minutesAgo: 5,
    invoiceIssued: true,
  },
  {
    id: "o-1232",
    number: 1232,
    source: "app",
    status: "new",
    pickupMethod: "dine_in",
    paymentMethod: "cash",
    customerName: "سعد المطيري",
    customerPhone: "0561239876",
    items: [
      { name: "كبسة دجاج", quantity: 2, unitPrice: 22 },
      { name: "مشروب غازي", quantity: 2, unitPrice: 5 },
    ],
    subtotal: 54,
    discount: 0,
    appFee: 0,
    total: 54,
    createdAt: "14:33",
    minutesAgo: 8,
    invoiceIssued: false,
  },
  {
    id: "o-1231",
    number: 1231,
    source: "app",
    status: "preparing",
    pickupMethod: "takeaway",
    paymentMethod: "cash",
    customerName: "محمد الشهري",
    customerPhone: "0509871122",
    items: [
      { name: "برجر دبل", quantity: 1, unitPrice: 24 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
      { name: "مشروب غازي", quantity: 1, unitPrice: 5 },
    ],
    subtotal: 36,
    discount: 0,
    appFee: 0.8,
    total: 36.8,
    createdAt: "14:29",
    minutesAgo: 12,
    debtRecorded: true,
    invoiceIssued: false,
  },
  {
    id: "o-1230",
    number: 1230,
    source: "app",
    status: "preparing",
    pickupMethod: "car",
    paymentMethod: "card",
    customerName: "عبدالعزيز الدوسري",
    carInfo: { type: "تويوتا كامري", color: "فضي", plate: "س ص م 8732" },
    items: [
      { name: "برجر دجاج مقرمش", quantity: 3, unitPrice: 19 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
    ],
    subtotal: 64,
    discount: 5,
    appFee: 0.8,
    total: 59.8,
    createdAt: "14:26",
    minutesAgo: 15,
    invoiceIssued: true,
  },
  {
    id: "o-1229",
    number: 1229,
    source: "app",
    status: "almost_ready",
    pickupMethod: "takeaway",
    paymentMethod: "wallet",
    customerName: "ريما الحربي",
    items: [
      { name: "ورق عنب", quantity: 2, unitPrice: 14 },
      { name: "كبسة دجاج", quantity: 1, unitPrice: 22 },
    ],
    subtotal: 50,
    discount: 0,
    appFee: 0.8,
    total: 50.8,
    createdAt: "14:21",
    minutesAgo: 20,
    invoiceIssued: true,
  },
  {
    id: "o-1228",
    number: 1228,
    source: "app",
    status: "ready",
    pickupMethod: "car",
    paymentMethod: "card",
    customerName: "خالد الغامدي",
    carInfo: { type: "هونداي سنتافي", color: "أسود", plate: "د ذ ر 1190" },
    items: [
      { name: "برجر كلاسيك", quantity: 4, unitPrice: 18 },
      { name: "بطاطس بالجبنة", quantity: 2, unitPrice: 11 },
      { name: "مشروب غازي", quantity: 4, unitPrice: 5 },
    ],
    subtotal: 114,
    discount: 0,
    appFee: 0.8,
    total: 114.8,
    createdAt: "14:16",
    minutesAgo: 25,
    pickupRemainingSeconds: 480,
    invoiceIssued: true,
  },
  {
    id: "o-1227",
    number: 1227,
    source: "app",
    status: "ready",
    pickupMethod: "takeaway",
    paymentMethod: "apple_pay",
    customerName: "لمى العنزي",
    items: [
      { name: "شاورما دجاج", quantity: 3, unitPrice: 16 },
      { name: "بطاطس مقلية", quantity: 2, unitPrice: 7 },
    ],
    subtotal: 62,
    discount: 0,
    appFee: 0.8,
    total: 62.8,
    createdAt: "14:13",
    minutesAgo: 28,
    pickupRemainingSeconds: 720,
    invoiceIssued: true,
  },
  {
    id: "o-1226",
    number: 1226,
    source: "pos",
    status: "picked_up",
    pickupMethod: "dine_in",
    paymentMethod: "pos_terminal",
    customerName: "زبون محلي",
    items: [
      { name: "كبسة دجاج", quantity: 3, unitPrice: 22 },
      { name: "ماء", quantity: 3, unitPrice: 2 },
    ],
    subtotal: 72,
    discount: 0,
    appFee: 0,
    total: 72,
    createdAt: "14:01",
    minutesAgo: 40,
    rating: 5,
    staffRating: 5,
    staffRatedBy: "عبدالله الحربي",
    staffRatedAt: "14:35",
    invoiceIssued: true,
  },
  {
    id: "o-1225",
    number: 1225,
    source: "pos",
    status: "picked_up",
    pickupMethod: "takeaway",
    paymentMethod: "cash",
    customerName: "زبون محلي",
    items: [
      { name: "برجر دبل", quantity: 2, unitPrice: 24 },
      { name: "بطاطس بالجبنة", quantity: 1, unitPrice: 11 },
      { name: "عصير برتقال", quantity: 2, unitPrice: 8 },
    ],
    subtotal: 75,
    discount: 0,
    appFee: 0,
    total: 75,
    createdAt: "13:51",
    minutesAgo: 50,
    rating: 4,
    invoiceIssued: true,
  },
  {
    id: "o-1224",
    number: 1224,
    source: "app",
    status: "picked_up",
    pickupMethod: "takeaway",
    paymentMethod: "wallet",
    customerName: "جود الدوسري",
    items: [
      { name: "برجر كلاسيك", quantity: 1, unitPrice: 18 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
      { name: "مشروب غازي", quantity: 1, unitPrice: 5 },
    ],
    subtotal: 30,
    discount: 0,
    appFee: 0.8,
    total: 30.8,
    createdAt: "13:36",
    minutesAgo: 65,
    rating: 3,
    invoiceIssued: true,
  },
  {
    id: "o-1223",
    number: 1223,
    source: "app",
    status: "not_picked",
    pickupMethod: "car",
    paymentMethod: "cash",
    customerName: "بندر العتيبي",
    customerPhone: "0551234567",
    carInfo: { type: "شيفروليه تاهو", color: "رمادي", plate: "و هـ ن 5530" },
    items: [
      { name: "شاورما دجاج", quantity: 2, unitPrice: 16 },
      { name: "عصير برتقال", quantity: 1, unitPrice: 8 },
    ],
    subtotal: 40,
    discount: 0,
    appFee: 0.8,
    total: 40.8,
    createdAt: "13:11",
    minutesAgo: 90,
    debtRecorded: false,
    invoiceIssued: false,
    customerDebtAmount: 51,
    walletDeducted: 51,
    walletShortfall: 0,
    customerBlocked: true,
  },
  {
    id: "o-1222",
    number: 1222,
    source: "pos",
    status: "preparing",
    pickupMethod: "dine_in",
    paymentMethod: "pos_terminal",
    customerName: "زبون محلي",
    items: [
      { name: "ورق عنب", quantity: 1, unitPrice: 14 },
      { name: "كبسة دجاج", quantity: 1, unitPrice: 22 },
      { name: "ماء", quantity: 1, unitPrice: 2 },
    ],
    subtotal: 38,
    discount: 0,
    appFee: 0,
    total: 38,
    createdAt: "14:23",
    minutesAgo: 18,
    invoiceIssued: true,
  },
];

export const mockIncomingOrders: Order[] = [
  {
    id: "o-incoming-1",
    number: 0,
    source: "app",
    status: "new",
    pickupMethod: "delivery",
    paymentMethod: "card",
    customerName: "تركي الزهراني",
    customerPhone: "0554443322",
    deliveryAddress: "الرياض — حي الصحافة، طريق أنس بن مالك، مبنى 8، الدور الأول، شقة 3",
    items: [
      { name: "برجر كلاسيك", quantity: 1, unitPrice: 18 },
      { name: "بطاطس مقلية", quantity: 1, unitPrice: 7 },
      { name: "مشروب غازي", quantity: 1, unitPrice: 5 },
    ],
    subtotal: 30,
    discount: 0,
    appFee: 0.8,
    deliveryFee: 15,
    total: 45.8,
    createdAt: "14:45",
    minutesAgo: 0,
    invoiceIssued: true,
  },
  {
    id: "o-incoming-2",
    number: 0,
    source: "app",
    status: "new",
    pickupMethod: "car",
    paymentMethod: "apple_pay",
    customerName: "هند الشمري",
    carInfo: { type: "جي إم سي يوكن", color: "أبيض", plate: "ل م ن 2014" },
    notes: "طلب بدون ثوم في البرجر.",
    items: [
      { name: "برجر دبل", quantity: 2, unitPrice: 24 },
      { name: "عصير برتقال", quantity: 2, unitPrice: 8 },
    ],
    subtotal: 64,
    discount: 0,
    appFee: 0.8,
    total: 64.8,
    createdAt: "14:45",
    minutesAgo: 0,
    invoiceIssued: true,
  },
];